import { SheetHandle, useSheetBehavior } from '@src/components/Sheet/SheetBehavior';
import { getPortalRoot } from '@src/components/portalRoot';
import { useLayoutEffect, useEffect, useId, useRef, useState, type ReactNode, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { Bar } from '@src/components/Bar/Bar';
import { Button } from '@src/components/Button/Button';
import { Checkbox } from '@src/components/Checkbox/Checkbox';
import { Cluster } from '@src/components/Cluster/Cluster';
import { CloseIcon } from '@src/components/icons/CloseIcon';
import { testIdAttr, testIdFor } from '@src/components/testId';
import { Typography } from '@src/components/Typography/Typography';
import './Dialog.css';

export type DialogSubmitVariant = 'primary' | 'danger';

/** What a screen passes to showDialog(). One host at the app root renders it. */
export interface DialogRequest {
  title: ReactNode;
  children?: ReactNode;
  /** Render interactive dialog content with the host submission state. */
  renderBody?: (busy: boolean, setSubmitDisabled: (disabled: boolean) => void, setSubmitSummary: (summary: string) => void) => ReactNode;
  /** Secondary action. Omit it for an info dialog that only needs Okay. */
  cancel?: string;
  /** Keep both footer actions visible, with the secondary on the left. */
  actionsLayout?: 'split';
  /** An additional action before the close/submit buttons. */
  secondaryAction?: { label: string; onClick: () => void };
  /** Primary action. Omit it when close / cancel is enough. */
  submit?: string;
  submitVariant?: DialogSubmitVariant;
  submitDisabled?: boolean;
  /** Require explicit acknowledgement before submitting. */
  confirmationLabel?: ReactNode;
  closeLabel?: string;
  /** Hide the X. Escape still dismisses. */
  hideClose?: boolean;
  /** Require an explicit body or submit action instead of Escape/close. */
  preventDismiss?: boolean;
  /** Use a full-screen flow with a scrollable body on phones. */
  mobileFullScreen?: boolean;
  /** Opt-in draggable half/full-height mobile sheet. */
  mobileSheet?: boolean;
  sheetInitiallyExpanded?: boolean;
  /** Start an expanding sheet at the height of its existing docked surface. */
  sheetOriginHeight?: number;
  fullScreen?: boolean;
  /** Hide the title bar and float the close button over full-screen content. */
  immersive?: boolean;
  onSubmit?: () => void | Promise<void>;
  onCancel?: () => void;
  testId?: string;
}

type Listener = (item: DialogRequest | null) => void;

// Keep subscribers and the open request together across Vite live updates.
// Existing callbacks can otherwise publish to a different store from the mounted host.
const dialogState: { listeners: Set<Listener>; current: DialogRequest | null; parents: DialogRequest[] } = import.meta.hot
  ?.data.dialogState ?? { listeners: new Set<Listener>(), current: null, parents: [] };
dialogState.parents ??= [];
if (import.meta.hot) import.meta.hot.data.dialogState = dialogState;

function notify(item: DialogRequest | null): void {
  dialogState.listeners.forEach((fn) => fn(item));
}

/** Open the app dialog. A second call replaces whatever is showing. */
export function showDialog(request: DialogRequest): void {
  dialogState.current = request;
  notify(request);
}

/** Change the active flow heading without replacing its content or state. */
export function setDialogTitle(title: ReactNode): void {
  if (dialogState.current) showDialog({ ...dialogState.current, title });
}

/** Cover the current dialog while keeping its component state and scroll position. */
export function pushDialog(request: DialogRequest): void {
  if (dialogState.current) dialogState.parents.push(dialogState.current);
  showDialog(request);
}

/** Dismiss the top dialog and restore its parent, if present. */
export function closeDialog(): void {
  dismissDialog(false);
}

function dismissDialog(animateSheet = false): void {
  const current = dialogState.current;
  const sheet = document.querySelector<HTMLElement>('[data-dialog-active="true"] .dialog--sheet');
  const finish = () => {
    if (dialogState.current !== current) return;
    dialogState.current = dialogState.parents.pop() ?? null;
    notify(dialogState.current);
  };
  if (!(animateSheet || current?.sheetOriginHeight !== undefined) || !sheet || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    finish();
    return;
  }
  if (sheet.dataset.sheetClosing) return;
  sheet.dataset.sheetClosing = 'true';
  // Keep the top layer mounted and the underlying sheet inert until exit ends.
  const timer = window.setTimeout(finish, 260);
  sheet.querySelector('.dialog__card')?.addEventListener('animationend', (event) => {
    if ((event as AnimationEvent).animationName !== 'sheet-slide-out') return;
    window.clearTimeout(timer);
    finish();
  }, { once: true });
}

/** Finish a nested flow and return to the page. */
export function closeAllDialogs(): void {
  dialogState.parents = [];
  dialogState.current = null;
  notify(null);
}

export function subscribeDialog(fn: Listener): () => void {
  dialogState.listeners.add(fn);
  if (dialogState.current) {
    fn(dialogState.current);
  }
  return () => {
    dialogState.listeners.delete(fn);
  };
}

/** True while the app dialog is on screen. Used to inert the page behind it. */
export function useDialogOpen(): boolean {
  const [open, setOpen] = useState(false);
  useEffect(() => subscribeDialog((item) => setOpen(item != null)), []);
  return open;
}

function focusablesIn(root: HTMLElement): HTMLElement[] {
  return Array.from(
    root.querySelectorAll<HTMLElement>(
      'button:not([disabled]), [href], input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
    ),
  ).filter((node) => !node.hasAttribute('disabled') && node.getAttribute('aria-hidden') !== 'true');
}

export interface DialogProps extends DialogRequest {
  busy?: boolean;
  onClose: (animateSheet?: boolean) => void;
}

/** The card itself. Screens do not mount this — DialogHost does. */
export function Dialog({
  title,
  children,
  renderBody,
  cancel,
  actionsLayout,
  secondaryAction,
  submit,
  submitVariant = 'primary',
  submitDisabled = false,
  confirmationLabel,
  closeLabel = 'Close',
  hideClose = false,
  preventDismiss = false,
  mobileFullScreen = false,
  mobileSheet = false,
  sheetInitiallyExpanded = false,
  sheetOriginHeight,
  fullScreen = false,
  immersive = false,
  busy = false,
  onClose,
  onCancel,
  onSubmit,
  testId,
}: DialogProps) {
  const titleId = useId();
  const [confirmed, setConfirmed] = useState(false);
  const [bodySubmitDisabled, setBodySubmitDisabled] = useState(false);
  const [submitSummary, setSubmitSummary] = useState('');
  const submissionBlocked = submitDisabled || bodySubmitDisabled || Boolean(confirmationLabel && !confirmed);
  const cardRef = useRef<HTMLFormElement>(null);
  const hasActions = Boolean(secondaryAction || cancel || submit);

  useEffect(() => {
    cardRef.current?.focus();
  }, []);

  function requestClose(animateSheet = false) {
    if (busy || preventDismiss) {
      return;
    }
    onCancel?.();
    onClose(animateSheet);
  }

  const sheet = useSheetBehavior(mobileSheet, () => requestClose(true), sheetInitiallyExpanded, sheetOriginHeight !== undefined);

  function submitForm() {
    if (busy || (preventDismiss && !submit)) {
      return;
    }
    if (submissionBlocked) {
      return;
    }
    if (onSubmit) {
      void onSubmit();
      return;
    }
    onClose();
  }

  return (
    <div
      className={`dialog${mobileFullScreen ? ' dialog--mobile-full-screen' : ''}${
        fullScreen ? ' dialog--full-screen' : ''
      }${immersive ? ' dialog--immersive' : ''}${sheet.active ? ' dialog--sheet' : ''}`}
      style={{ ...sheet.style, ...(sheetOriginHeight === undefined ? {} : { '--sheet-entry-offset': `max(0px, calc(var(--sheet-height) - ${sheetOriginHeight}px))` }) } as CSSProperties}
      data-sheet-dragging={sheet.dragging || undefined}
      data-sheet-entered={sheet.entered || undefined}
      {...testIdAttr(testId)}
    >
      <div
        className="dialog__scrim"
        onClick={sheet.active ? () => requestClose() : undefined}
        aria-hidden="true"
        {...testIdAttr(testIdFor(testId, 'scrim'))}
      />
      <form
        ref={cardRef}
        className="dialog__card"
        onAnimationEnd={event => { if (event.target === event.currentTarget && event.animationName === 'sheet-slide-in') sheet.finishEntrance(); }}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onSubmit={(event) => {
          event.preventDefault();
          submitForm();
        }}
        {...testIdAttr(testIdFor(testId, 'card'))}
      >
        {sheet.active && <SheetHandle behavior={sheet} disabled={busy || preventDismiss} />}
        <Bar wrap={false} align="center" gap={4} className="dialog__header">
          <Typography as="h2" role="title/card" id={titleId} className="dialog__title">
            {title}
          </Typography>
          <div className="dialog__header-accessory" />
          {hideClose ? null : (
            <Button
              type="button"
              variant="ghost"
              size="small"
              className="dialog__close"
              icon={<CloseIcon />}
              aria-label={closeLabel}
              disabled={busy}
              onClick={() => requestClose()}
              testId={testIdFor(testId, 'close')}
            />
          )}
        </Bar>
        {children || renderBody ? (
          <Typography as="div" role="body/base" className="dialog__body">
            {renderBody ? renderBody(busy, setBodySubmitDisabled, setSubmitSummary) : children}
          </Typography>
        ) : null}
        {confirmationLabel ? (
          <Checkbox
            checked={confirmed}
            onCheckedChange={setConfirmed}
            label={confirmationLabel}
            disabled={busy}
            testId={testIdFor(testId, 'confirmation')}
          />
        ) : null}
        {hasActions ? (
          <Cluster
            gap={3}
            justify={actionsLayout === 'split' ? 'between' : 'end'}
            wide
            stack={actionsLayout === 'split' ? undefined : 'narrow'}
            className={`dialog__actions${actionsLayout === 'split' ? ' dialog__actions--split' : ''}`}
            testId={testIdFor(testId, 'actions')}
          >
            {submitSummary && <p className="dialog__submit-summary" aria-live="polite" {...testIdAttr(testIdFor(testId, 'submit-summary'))}>{submitSummary}</p>}
            {secondaryAction ? (
              <Button
                type="button"
                variant="secondary"
                size="large"
                disabled={busy}
                onClick={secondaryAction.onClick}
                testId={testIdFor(testId, 'secondary')}
              >
                {secondaryAction.label}
              </Button>
            ) : null}
            {cancel ? (
              <Button
                type="button"
                variant="secondary"
                size="large"
                className="dialog__cancel"
                disabled={busy}
                onClick={() => requestClose()}
                testId={testIdFor(testId, 'cancel')}
              >
                {cancel}
              </Button>
            ) : null}
            {submit ? (
              <Button
                type="submit"
                variant={submitVariant}
                size="large"
                disabled={busy || submissionBlocked}
                testId={testIdFor(testId, 'submit')}
              >
                {submit}
              </Button>
            ) : null}
          </Cluster>
        ) : null}
      </form>
    </div>
  );
}

/** Sits next to ToastHost. Screens call showDialog() — they do not render a dialog. */
export function DialogHost() {
  const [item, setItem] = useState<DialogRequest | null>(null);
  const [busy, setBusy] = useState(false);
  const itemRef = useRef<DialogRequest | null>(null);
  const busyRef = useRef(false);
  useLayoutEffect(() => {
    itemRef.current = item;
    busyRef.current = busy;
  }, [item, busy]);

  useEffect(
    () =>
      subscribeDialog((next) => {
        setItem(next);
        setBusy(false);
      }),
    [],
  );

  const open = item != null;
  useLayoutEffect(() => {
    const card = document.querySelector<HTMLElement>('[data-dialog-active="true"] .dialog__card');
    // Updating controlled content must not blur the field being edited.
    // Still move focus into newly opened or restored nested dialogs.
    if (item && card && !card.contains(document.activeElement)) {
      card.focus({ preventScroll: true });
    }
  }, [item]);

  useEffect(() => {
    if (!open) {
      delete document.documentElement.dataset.dialog;
      return undefined;
    }

    document.documentElement.dataset.dialog = 'open';

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault();
        if (!busyRef.current && !itemRef.current?.preventDismiss) {
          itemRef.current?.onCancel?.();
          closeDialog();
        }
        return;
      }
      if (event.key !== 'Tab') {
        return;
      }
      const card = document.querySelector<HTMLElement>('[data-dialog-active="true"] .dialog__card');
      if (!card) {
        return;
      }
      const nodes = focusablesIn(card);
      if (nodes.length === 0) {
        event.preventDefault();
        card.focus();
        return;
      }
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || !card.contains(active))) {
        event.preventDefault();
        last.focus();
        return;
      }
      if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => {
      delete document.documentElement.dataset.dialog;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  if (!item) {
    return null;
  }

  const requests = [...dialogState.parents, item];
  return createPortal(
    requests.map((request, index) => <div key={index} className={index !== requests.length - 1 && request.mobileSheet && requests[index + 1]?.mobileSheet ? "dialog-layer dialog-layer--sheet-behind" : "dialog-layer"} hidden={index !== requests.length - 1 && !(request.mobileSheet && requests[index + 1]?.mobileSheet)} aria-hidden={index !== requests.length - 1 || undefined} inert={index !== requests.length - 1 || undefined} data-dialog-active={index === requests.length - 1 ? 'true' : undefined}>
      <Dialog
        key={request.testId}
        {...request}
        busy={index === requests.length - 1 && busy}
        onClose={dismissDialog}
        onSubmit={request.onSubmit ? async () => {
          setBusy(true);
          try {
            await request.onSubmit?.();
            // A submit action may replace this dialog with the next step.
            if (dialogState.current === request) closeDialog();
          } catch {
            setBusy(false);
          }
        } : undefined}
      />
    </div>),
    getPortalRoot(),
  );
}
